import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { DatePipe, DOCUMENT } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { ngxLightOptions } from "ngx-light-carousel";
import { NGXLogger } from "ngx-logger";
import { SlickCarouselComponent } from "ngx-slick-carousel";
import { Observable, of, zip } from "rxjs";
import { catchError, timeout } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { AuthenticationService, NewsService } from "../../../core";
import {
  shareSocialMedia,
  SocialMediaProviders,
  textFromHtml
} from "../../../core/_helpers/share-helper";
import { Directions } from "../../../core/models/enums/direction.enum";
import { ImageSEO } from "../../../core/models/image-seo.model";
import { DemoDialogService } from "../../../core/services/dialog";
import { SiilaTranslationService } from "../../../core/services/siila-translation.service";
import { StaticImagesSEOService } from "../../../core/services/static-images.service";
import { MoreNews } from "../../models/more-news.model";
import { NewsTopics } from "../../models/news-topics.model";
import { ImageDTO, NewsDetail, NewsSection } from "../../models/news.model";
import { I18nService } from "../../services";
import { AnalyticsService } from "../../services/analytics.service";
import {
  BreadcrumbItem,
  MetaTagService
} from "../../services/meta-tag.service";
import { PrerenderReadyService } from "../../services/prerender-ready.service";
import { NewsNavigateService } from "../../services/news.navigate.service";
import { action, page } from "../../utils/params";
import { isNumericRouteId } from "../../utils/route-id";
import {
  clearPrerenderRedirect,
  emitPrerenderRedirect,
  isPrerenderAgent
} from "../../utils/prerender";
import { DemoDialogComponent } from "../dialogs";

interface SocialMediaLinkOptions {
  title?: string;
  mailTo?: string;
  subject?: string;
  body?: string;
}

interface SocialMediaLink {
  alt: string;
  title: string;
  img: string;
  providerId: string;
  link: string;
  options: SocialMediaLinkOptions;
}

@Component({
  selector: "app-detail-news",
  templateUrl: "./detail-news.component.html",
  styleUrls: ["./detail-news.component.scss"]
})
export class DetailNewsComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @ViewChild("mainDiv") mainDiv: ElementRef;
  @ViewChild("slickCarousel", { static: true })
  carousel: SlickCarouselComponent;
  currentCode: string = "";
  private prerenderMarked = false;
  private readonly prerenderStart = performance.now(); // component construction time
  news: NewsDetail = new NewsDetail();
  mostRead: MoreNews[] = [];
  topics: NewsTopics[] = [];
  isExpanded: boolean = false;
  isMobile = false;
  idNews: string;
  sectionImages: ImageDTO[];
  currentIndex = 0;
  headerImgCaption: string;
  browserLang: string;

  slideConfig = {
    slidesToShow: 1,
    slidesToScroll: 1
  };

  socialMediaLinks: SocialMediaLink[] = [];

  defaultSlide: Partial<ngxLightOptions>;
  siilaBrandSEO: ImageSEO;
  spotTransformingSEO: ImageSEO;
  siilaAcademyBannerSEO: ImageSEO;
  trustedUrl: SafeResourceUrl;
  unsafeUrl = "https://player.vimeo.com/video/822080532"; // Reemplaza con tu URL
  show = true;
  translatedPrimaryContent: string;
  public $isLoggedIn: Observable<boolean>;
  isPropertyType = false;
  userIsLoggedIn: boolean;
  bannerOod: ImageSEO;
  bannerPairs: ImageSEO;
  marketAnalyticsSEO: ImageSEO;
  latamCountry = "latamBr";
  isServiceUnavailable: boolean = false;
  breadcrumbItems: BreadcrumbItem[] = [];

  constructor(
    private newsService: NewsService,
    private i18: I18nService,
    private _cdr: ChangeDetectorRef,
    public breakpointObserver: BreakpointObserver,
    private route: ActivatedRoute,
    private router: Router,
    private siilaTranslationService: SiilaTranslationService,
    private datePipe: DatePipe,
    private metaSrv: MetaTagService,
    public sanitizer: DomSanitizer,
    private staticImgSrv: StaticImagesSEOService,
    private analyticsSrv: AnalyticsService,
    @Inject(DOCUMENT) private document: Document,
    private newsNavigate: NewsNavigateService,
    private demo: DemoDialogService,
    private ngxLogger: NGXLogger,
    private auth: AuthenticationService,
    private prerenderReadyService: PrerenderReadyService
  ) {
    this.currentCode = environment.platformId;
    this.breakpointObserver
      .observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape])
      .subscribe(result => {
        this.isMobile = result.matches;
      });

    this.defaultSlide = {
      arrows: true,
      infinite: false
    };

    this.browserLang = this.siilaTranslationService.getCurrentLanguage();
    this.trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.unsafeUrl
    );
    this.$isLoggedIn = this.auth.$isLoginSubject;
    this.$isLoggedIn.subscribe(isLoggedIn => {
      if (isLoggedIn) {
        this.userIsLoggedIn = true;
        let user = JSON.parse(localStorage.getItem("user"));
        this.newsService
          .getSubscription(user.companyId)
          .subscribe((resp: unknown[]) => {
            this.isPropertyType =
              resp.length > 0 && user?.roles?.includes("ROLE_USER");
          });
      } else {
        this.userIsLoggedIn = false;
      }
    });
  }
  ngAfterViewInit() {}

  ngAfterViewChecked(): void {
    // Signal Prerender.io once the article has actually resolved. Runs every
    // change-detection cycle, so gate on the loaded article id and a one-shot
    // boolean (news starts as an empty NewsDetail(), so check news.id).
    if (this.news && this.news.id && !this.prerenderMarked) {
      this.prerenderMarked = true;
      const elapsedMs = Math.round(performance.now() - this.prerenderStart);
      this.ngxLogger.debug(
        `Prerender: ready after ${elapsedMs}ms for ${window.location.href}`
      );
      this.prerenderReadyService.signalReady();
    }
  }

  ngOnDestroy() {
    // Leaving the article must not leak the restriction to other pages.
    this.newsService.setHideSpanishMenu(false);
    this.metaSrv.removeNewsArticleJsonLd();
    this.metaSrv.removeNewsArticleOgTags();
  }

  ngOnInit() {
    this.setStorageBanner();
    this.bannerOod = this.staticImgSrv.getBannerOdd();
    this.bannerPairs = this.staticImgSrv.getBannerPairs();
    if (this.currentCode !== "co") {
      this.analyticsSrv.trackBannerImpression(
        this.bannerOod,
        1,
        this.currentCode
      );
      this.analyticsSrv.trackBannerImpression(
        this.bannerPairs,
        2,
        this.currentCode
      );
    }
    this.marketAnalyticsSEO = this.staticImgSrv.getMultifamilySEO();
    const whatsappSEO = this.staticImgSrv.getShareWhatsappSEO();
    const shareEmailSEO = this.staticImgSrv.getShareEmailSEO();
    const facebookSEO = this.staticImgSrv.getShareFacebookSEO();
    const linkedSEO = this.staticImgSrv.getShareLinkedinSEO();
    this.socialMediaLinks = [
      {
        alt: whatsappSEO.alt,
        title: whatsappSEO.title,
        img: "../../../assets/icons/whatsapp.png",
        providerId: "whatsapp",
        link: this.document.location.href,
        options: {
          title: null
        }
      },
      {
        alt: shareEmailSEO.alt,
        title: shareEmailSEO.title,
        img: "../../../assets/icons/mail.png",
        providerId: "email",
        link: "mailto:",
        options: {
          mailTo: "",
          subject: null,
          body: null
        }
      },
      {
        alt: facebookSEO.alt,
        title: facebookSEO.title,
        img: "../../../assets/icons/facebook.png",
        providerId: "facebook",
        link:
          this.currentCode == "br"
            ? "https://web.facebook.com/SiiLA.BR/"
            : "https://www.facebook.com/SiiLA.mx",
        options: {
          title: null
        }
      },
      {
        alt: linkedSEO.alt,
        title: linkedSEO.title,
        img: "../../../assets/icons/in.png",
        providerId: "linkedin",
        link:
          this.currentCode == "br"
            ? "https://www.linkedin.com/company/siila-br"
            : "https://www.linkedin.com/company/siila-mx",
        options: {
          title: null
        }
      }
    ];
    this.siilaBrandSEO = this.staticImgSrv.getMultifamilySEO();
    this.spotTransformingSEO = this.staticImgSrv.getSpotTransformingExperienceSEO();
    this.siilaAcademyBannerSEO = this.staticImgSrv.getAcademyImgSEO();
    this.route.params.subscribe(params => {
      this.idNews = params.id;
      // No addressable article id — answer 404 without spending a request on a
      // lookup that cannot match. `/news/lang/en` is THIS route with
      // `title = "lang"` and `id = "en"` (there is no `/news` listing route),
      // and `NewsService.getNewsId` returns `undefined` rather than an
      // observable for a NaN id, which broke the zip below with a TypeError:
      // no `status`, so the error branch answered 503 instead of 404. Same
      // guard the product and solution detail routes use — see
      // `isNumericRouteId`.
      if (!isNumericRouteId(this.idNews)) {
        this.navigateToNotFoundPage();
        return;
      }
      this.getNews();
      this.getTopics();
    });

    this.newsService.changesLang$.subscribe(val => {
      if (val) {
        this.browserLang = this.siilaTranslationService.getCurrentLanguage();
        this.openNews(
          this.news.id,
          this.siilaTranslationService.getTranslation(this.news.headline),
          true
        );
      }
    });

    setTimeout(() => {
      this.configureWistiaAutoplay();
    }, 2000);
  }

  configureWistiaAutoplay(): void {
    if (typeof (window as any).Wistia !== "undefined") {
      if (this.bannerOod?.type === "wistia" && this.bannerOod.wistiaId) {
        this.tryPlayWistiaVideo(this.bannerOod.wistiaId, 0);
      }

      if (this.bannerPairs?.type === "wistia" && this.bannerPairs.wistiaId) {
        this.tryPlayWistiaVideo(this.bannerPairs.wistiaId, 0);
      }
    } else {
      setTimeout(() => {
        this.configureWistiaAutoplay();
      }, 1000);
    }
  }

  tryPlayWistiaVideo(videoId: string, attempt: number): void {
    if (attempt >= 10) {
      return;
    }

    const wistia = (window as any).Wistia;
    const video = wistia.api(videoId);

    if (video) {
      video.mute();
      video.play();
      video.bind("end", () => {
        video.time(0);
        video.play();
      });
    } else {
      setTimeout(() => {
        this.tryPlayWistiaVideo(videoId, attempt + 1);
      }, 500);
    }
  }

  setStorageBanner() {
    const totalCycle = this.getBannerCycleLength();
    const current = Number(localStorage.getItem("bannerCount")) || 0;
    const next = (current % totalCycle) + 1;
    localStorage.setItem("bannerCount", next.toString());
  }

  private getBannerCycleLength(): number {
    const totalBanners = this.currentCode === "br" ? 7 : 6;
    return totalBanners % 2 === 0
      ? totalBanners * (totalBanners / 2)
      : totalBanners * totalBanners;
  }

  private navigateToNotFoundPage(): void {
    this.metaSrv.removeNewsArticleJsonLd();
    this.metaSrv.removeNewsArticleOgTags();
    this.router.navigateByUrl("/404", {
      state: { badURL: window.location.href }
    });
  }

  getNews() {
    this.translatedPrimaryContent = "";
    this.metaSrv.removeBreadcrumbListJsonLd();
    // Clear any prior restriction before the new article resolves, so navigating
    // from a latam BR-news article to a non-BR one re-shows Spanish in the menu.
    this.newsService.setHideSpanishMenu(false);

    const isPreviewMode = window.location.href.includes(
      "insight-preview/detail"
    );

    zip(
      this.newsService.getNewsId(Number(this.idNews)),
      // The "most read" rail must never gate the ARTICLE. `zip` waits for both
      // sources, so a slow secondary endpoint used to hold the whole page — and
      // with it every prerender signal, since the 301/404/503 metas are only
      // emitted from the callbacks below — while a failing one pushed a
      // perfectly healthy article into the 503 branch. Bounded and defaulted,
      // it can now only ever cost the rail itself. The bound is deliberately
      // UNDER `PrerenderReadyService.CEILING_MS` (2000): a rail that hangs must
      // give up in time for the article to still render inside the window
      // Prerender.io snapshots in, or the crawler captures an empty page.
      this.newsService.getMostNews().pipe(
        timeout(1500),
        catchError(() => of([]))
      )
    )
      .pipe(timeout(20000)) // bound the wait; a hung backend degrades to the 503 branch in ~20s
      .subscribe(
        resp => {
          this.news = resp[0] as NewsDetail;

          if (!this.news || !this.news.id) {
            this.navigateToNotFoundPage();
            return;
          }

          if (this.news) {
            // An unpublished article (DRAFT and any other non-PUBLISHED state)
            // is not a public resource: answer 404 instead of redirecting to
            // /resource. The old `location.href = "/resource"` was a hard
            // navigation, so Prerender.io snapshotted the REsource listing under
            // the article's URL as a soft-200 — every draft id indexed as
            // duplicate content. `navigateToNotFoundPage` routes in-app, so the
            // 404 meta PageNotFoundComponent emits survives in the same document.
            // The preview route intentionally serves unpublished articles.
            if (this.news.status !== "PUBLISHED" && !isPreviewMode) {
              this.navigateToNotFoundPage();
              return;
            }

            this.translatedPrimaryContent = this.siilaTranslationService.getTranslation(
              this.news.primaryContent
            );
          }
          // latam + BR news (regionOriginId 101) → hide Spanish in the language menu.
          this.newsService.setHideSpanishMenu(
            this.currentCode === "latam" && this.news?.regionOriginId === 101
          );
          this.metaSrv.addCanonicalTag(this.canonicalizeURL(isPreviewMode));
          this.news.sectionsInfo.map(x =>
            x.videoURL != null && x.videoURL != ""
              ? (x.videoURL = this.sanitizeUrl(x.videoURL))
              : ""
          );

          if (this.news.headerImageCaption) {
            this.headerImgCaption = this.siilaTranslationService.getTranslation(
              this.news.headerImageCaption
            );
          }
          if (this.news.sectionsInfo) {
            try {
              this.sectionImages = this.news.sectionsInfo.map(
                (ns: NewsSection) => ns.sectionImgsRES
              )[0];
            } catch (error) {}
          }
          this.metaSrv.setInsightsDetailTags(this.news);
          this.metaSrv.setNewsArticleJsonLd(this.news);
          this.metaSrv.setNewsArticleOgTags(this.news);
          // Article-aware hreflang: build each language's URL from the translated
          // headline so the EN alternate uses /news/<en-title> and PT/ES use
          // /noticias/<title>. Independent of which slug alias the visitor arrived on.
          this.metaSrv.addHreflangTags(
            `news/${this.idNews}`,
            lang =>
              this.siilaTranslationService.buildNewsDetailPath(
                this.news.urlTitle ? this.news.urlTitle : this.news.headline,
                this.idNews,
                lang
              ),
            this.news.regionOriginId
          );
          this.breadcrumbItems = this.getBreadcrumbItems();
          this.metaSrv.setBreadcrumbListJsonLd(this.breadcrumbItems);
          this.mostRead = resp[1] as MoreNews[];
          setTimeout(() => {
            this.addBlanktoLinks();
          }, 1000);
        },
        (err: HttpErrorResponse) => {
          if (err.status === 404) {
            this.navigateToNotFoundPage();
            return;
          }
          // 5xx / network error → service temporarily unavailable (stay on URL).
          // `setPrerenderStatusCode` also clears any 301 pair emitted earlier.
          this.isServiceUnavailable = true;
          this.metaSrv.removeNewsArticleJsonLd();
          this.metaSrv.removeNewsArticleOgTags();
          this.metaSrv.setPrerenderStatusCode(503);
          this.ngxLogger.error("DetailNews: article fetch failed", err);
        }
      );
  }

  /**
   * Bring the address bar to the article's ONE canonical URL and, for crawlers,
   * answer with a real 301. Everything except the id is rebuilt: the slug word
   * (`news`/`noticias`), the title slug and the `/lang` suffix all come from the
   * article data, so any combination of stale slug, wrong word or contradictory
   * suffix converges on the same target. Returns the canonical URL for the
   * caller to use as the `rel=canonical` value.
   *
   * The `301 + Location` meta pair is emitted UNCONDITIONALLY whenever the URL
   * is off-canonical, not just when the renderer is detected: Prerender.io
   * forwards the crawler's own user agent here, so the page cannot tell a bot
   * from a person (verified on staging — a Googlebot request rendered with
   * Googlebot's UA and no tags were emitted). The tags are inert in a browser,
   * and `replaceState` leaves the document — and therefore the tags — intact, so
   * the crawler's snapshot carries the redirect while the visitor just sees the
   * address bar corrected.
   *
   * Outcomes:
   *  - already canonical -> clear any stale tags, nothing else to do;
   *  - URL is off but the language on screen is right -> `replaceState`;
   *  - the language itself must change (only when the requested one does not
   *    exist) -> a real `location.replace`, skipped when we can tell we are being
   *    prerendered, since a navigation would discard the tags just emitted.
   */
  canonicalizeURL(isPreviewMode: boolean): string {
    const currentUrl = window.location.href;
    // The preview route intentionally serves unpublished articles at their
    // own URL — canonicalizing it would redirect editors out of the preview.
    if (!this.idNews || isPreviewMode || !this.news) {
      return currentUrl;
    }

    const sts = this.siilaTranslationService;
    const targetLang = this.resolveArticleLanguage();
    const canonicalPath = sts.buildLocalizedPath(
      `/${sts.buildNewsDetailPath(
        this.articleTitleSource(),
        this.idNews,
        targetLang
      )}`,
      targetLang
    );
    const search = window.location.search || "";
    const canonicalUrl = `${window.location.origin}${canonicalPath}${search}`;

    if (this.isAlreadyCanonical(canonicalPath)) {
      clearPrerenderRedirect(this.document);
      return canonicalUrl;
    }

    emitPrerenderRedirect(this.document, canonicalUrl);

    if (targetLang !== sts.getCurrentLanguage()) {
      if (!isPrerenderAgent()) {
        window.location.replace(canonicalUrl);
      }
      return canonicalUrl;
    }

    sts.replacePathKeepingQuery(canonicalPath);
    return canonicalUrl;
  }

  /**
   * The language this URL should resolve to: what the slug word asks for, unless
   * the article has no such version — then the closest language that DOES exist
   * (deployment default first), so a missing translation redirects to the real
   * article instead of rendering an English shell around foreign-language text.
   * Mirrors the latam BR-news rule in `addHreflangTags`: region 101 articles have
   * no Spanish version even when the JSON carries an `es` key.
   */
  private resolveArticleLanguage(): string {
    const sts = this.siilaTranslationService;
    const requested =
      sts.getNewsSlugLanguage(window.location.pathname) ||
      sts.getCurrentLanguage();
    if (this.articleHasLanguage(requested)) {
      return requested;
    }
    const fallbacks = [
      sts.getDefaultLanguage(),
      ...sts.getSupportedLanguages()
    ];
    return fallbacks.find(lang => this.articleHasLanguage(lang)) || requested;
  }

  private articleHasLanguage(lang: string): boolean {
    const isBrazilOnLatam =
      this.currentCode === "latam" && this.news?.regionOriginId === 101;
    if (isBrazilOnLatam && lang === "es") {
      return false;
    }
    return this.siilaTranslationService.hasTranslation(
      this.articleTitleSource(),
      lang
    );
  }

  /** `urlTitle` when the article has one, else the headline — the same source
   * `buildNewsDetailPath` slugifies, so availability and slug never disagree. */
  private articleTitleSource(): string {
    return this.news.urlTitle ? this.news.urlTitle : this.news.headline;
  }

  /**
   * Compare the live path against the canonical one, ignoring the differences a
   * URL picks up in transit: percent-encoding (`not%C3%ADcias`), a trailing
   * slash, and case.
   */
  private isAlreadyCanonical(canonicalPath: string): boolean {
    return (
      this.normalizePath(window.location.pathname) ===
      this.normalizePath(canonicalPath)
    );
  }

  private normalizePath(path: string): string {
    let decoded = path;
    try {
      decoded = decodeURIComponent(path);
    } catch (e) {}
    return decoded.replace(/\/+$/, "").toLowerCase();
  }

  addBlanktoLinks() {
    const links = this.mainDiv.nativeElement.querySelectorAll("a");
    links.forEach(link => {
      link.setAttribute("target", "_blank");
    });
  }

  getTopics() {
    this.newsService.getNewsTopics().subscribe(resp => {
      this.topics = resp as NewsTopics[];
    });
  }

  headerImageNews(item: string, size = false): string {
    const url = item.replace("***", size ? "1280w" : "640w");
    return `${environment.cloudfrontURL}${url}`;
  }
  playButton(isExpanded: boolean) {
    this.isExpanded = !isExpanded;
  }

  openNews(id: number, title, reload = false) {
    this.newsNavigate.openNews(
      id,
      title.urlTitle ? title.urlTitle : title.headline,
      this.isMobile || reload,
      "",
      false,
      this.browserLang
    );
  }

  getTranslation(value: string) {
    const r = JSON.parse(value)[
      this.siilaTranslationService.getCurrentLanguage()
    ];
    return r;
  }

  getLanguage() {
    return this.siilaTranslationService.getCurrentLanguage();
  }

  formatInternalAuthorName(name: string): string {
    if (!name || name === "SiiLA News") {
      return name;
    }
    return this.i18.get("news.authorWithSiilaNews", { name });
  }

  formatDate(date, format) {
    return this.datePipe.transform(date, format);
  }

  openSite() {
    const currentLang = this.siilaTranslationService.getCurrentLanguage();
    window.open(
      this.currentCode == "br"
        ? this.siilaTranslationService.buildCrossCountryUrl(
            "br",
            "",
            currentLang
          )
        : this.siilaTranslationService.buildCrossCountryUrl(
            "mx",
            "",
            currentLang
          ),
      this.isMobile ? "_self" : "_blank"
    );
  }

  openLatam() {
    window.open(
      this.siilaTranslationService.buildCrossCountryUrl("latam", ""),
      this.isMobile ? "_self" : "_blank"
    );
  }

  // `tags` is the ONE segment the tag listing is canonical on. This used to
  // default to the singular `tag`, which reached the same page at a second URL;
  // HomeNewsComponent still 301s that form for links already in the wild.
  goNewsTags(tag, color, type = "tags") {
    const basePath = `resource/${type}/${this.siilaTranslationService.getTranslation(
      tag.name
    )}/${tag.id}/${color}`;
    window.open(
      this.siilaTranslationService.buildLocalizedPath(basePath),
      this.isMobile ? "_self" : "_blank"
    );
  }

  goHome(latam = false) {
    window.open(
      latam
        ? this.siilaTranslationService.buildCrossCountryUrl(
            "latam",
            "/resource"
          )
        : this.siilaTranslationService.buildLocalizedPath("resource"),
      "_self"
    );
  }

  isLastImage(): boolean {
    return this.currentIndex === this.sectionImages.length - 1;
  }

  isFirstImage(): boolean {
    return this.currentIndex === 0;
  }

  nextImage(): void {
    if (!this.isLastImage()) {
      this.currentIndex++;
    }
  }

  prevImage(): void {
    if (!this.isFirstImage()) {
      this.currentIndex--;
    }
  }

  prevSlide(carousel: SlickCarouselComponent) {
    if (!carousel) {
      return;
    }
    carousel.slickPrev();
  }

  loadPage(direction: string, carousel: SlickCarouselComponent) {
    switch (direction) {
      case Directions.Next:
        const lastSlideActive = this.isLastImage();

        if (!lastSlideActive) {
          carousel.slickNext();
          this._cdr.detectChanges();
        } else if (lastSlideActive) {
          carousel.slickNext();
        } else {
          //this.appendPage(Directions.Next, carousel);
        }
        break;
      case Directions.Back:
        this.prevSlide(carousel);
        break;
    }
  }

  shareLink(socialLink) {
    /* const articleTitle = this.getDefaultTranslation(this.article.title);
    const content = this.getDefaultTranslation(this.article.content); */

    switch (socialLink.providerId) {
      case SocialMediaProviders.Facebook:
      case SocialMediaProviders.LinkedIn:
        socialLink.options.body = textFromHtml(this.getDefaultTranslation(""));
        socialLink.options.url = window.location.href;
        socialLink.options.title = this.getDefaultTranslation(
          this.news.headline
        );
        shareSocialMedia(socialLink);
        break;
      case SocialMediaProviders.Email:
        const subject = `SiiLA News: ${this.getDefaultTranslation(
          this.news.headline
        )}`;
        const body =
          textFromHtml(
            `${this.getDefaultTranslation(this.news.primaryContent)}`
          ).substring(0, 300) +
          textFromHtml(
            ` ...\n\n<br><br><a href="${window.location.href}">${window.location.href}</a>`
          );
        const mailtoUrl = `mailto:?subject=${encodeURIComponent(
          subject
        )}&body=${encodeURIComponent(body)}`;

        window.location.href = mailtoUrl;
        break;
      case SocialMediaProviders.Whatsapp:
        window.open(
          `https://api.whatsapp.com/send?text=${this.getDefaultTranslation(
            this.getDefaultTranslation(this.news.headline)
          )}%20${socialLink.link}`,
          "_blank"
        );
        break;
      default:
        break;
    }
  }

  getAltTxt(text: string) {
    return this.siilaTranslationService.getTranslation(text);
  }

  getDefaultTranslation(translationArtifact) {
    try {
      return this.siilaTranslationService.getTranslation(translationArtifact);
    } catch (e) {}
  }

  isEnglishLang() {
    return this.i18.getCurrentLanguage() === "en";
  }

  isBR() {
    return this.currentCode && this.currentCode.toLowerCase() === "br";
  }

  openSpot() {
    if (this.isBR()) {
      var url = this.isEnglishLang()
        ? "https://spot.siila.com.br/en"
        : "https://spot.siila.com.br/pt-br";
      window.open(url, this.isMobile ? "_self" : "_blank");
    } else {
      window.open(
        this.i18.buildLocalizedPath("/SPOT"),
        this.isMobile ? "_self" : "_blank"
      );
    }
  }

  openProduct() {
    window.open(
      this.i18.buildLocalizedPath("/marketanalytics"),
      this.isMobile ? "_self" : "_blank"
    );
  }

  openAcademy() {
    window.open(
      "https://mailchi.mp/siila/academy-advanced",
      this.isMobile ? "_self" : "_blank"
    );
  }

  openMultifamilyBanner() {
    let url = this.isEnglishLang()
      ? "https://mailchi.mp/siila.com.br/7gms21uue1"
      : "https://mailchi.mp/siila.com.br/multifamily";
    window.open(url, this.isMobile ? "_self" : "_blank");
  }

  sanitizeUrl(url): SafeResourceUrl {
    const trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

    return trustedUrl;
  }

  isIOS(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  }

  openContactDialog() {
    this.demo.open(
      DemoDialogComponent,
      false,
      page.resource,
      action.requestDemo,
      "",
      this.i18.get(`analytics.${this.currentCode}.analytic-list.titleModal`)
    );
  }

  login() {
    if (this.currentCode == "latam") {
      const region = this.news.contentTags?.regionOrigin;
      const redirectURL =
        region == 102 ? environment.redirectURLmx : environment.redirectURL;

      window.open(`${redirectURL}?newscallback=${this.news.id}`, "_self");
    } else {
      window.open(
        `${environment.redirectURL}?newscallback=${this.news.id}`,
        "_self"
      );
    }
  }

  validateOtherTopics(topic) {
    if (topic == "OUTROS" || topic == "OTROS" || topic == "OTHER") {
      return false;
    }

    return true;
  }

  validateCarousel() {
    return (
      (!this.userIsLoggedIn &&
        (this.news?.subscriberOnly == null ||
          this.news?.subscriberOnly == false)) ||
      (this.userIsLoggedIn && this.isPropertyType)
    );
  }

  /**
   * @param  {} event
   */
  slickInit(event) {}

  logintest() {
    this.auth.$isLoginSubject.next(true);
  }

  openBanner(banner: ImageSEO, position: number): void {
    if (this.currentCode !== "co") {
      this.analyticsSrv.trackBannerClick(banner, position, this.currentCode);
    }
    let resolvedLink = banner.link;
    if (
      resolvedLink &&
      !resolvedLink.startsWith("http") &&
      !resolvedLink.startsWith("/")
    ) {
      resolvedLink = "https://" + resolvedLink;
    }
    window.open(resolvedLink, this.isMobile ? "_self" : "_blank");
  }

  onVideoLoaded(video: HTMLVideoElement): void {
    video.muted = true;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.then === "function") {
      playPromise.catch(err => {
        console.warn("Autoplay blocked by the browser:", err);
      });
    }
  }

  getBreadcrumbItems(): BreadcrumbItem[] {
    const domainRoot = environment.applicationURL.replace(/\/+$/, "");
    const newsPath = this.siilaTranslationService.buildLocalizedPath(
      "/resource"
    );
    return [
      { label: this.i18.get("news.breadcrumbHome"), url: domainRoot },
      {
        label: this.i18.get("news.breadcrumbNews"),
        url: `${domainRoot}${newsPath.startsWith("/") ? "" : "/"}${newsPath}`
      },
      { label: this.siilaTranslationService.getTranslation(this.news.headline) }
    ];
  }

  getPublishDateDisplayFormat(): string {
    return this.getLanguage() === "en" ? "MM/dd/yyyy" : "dd/MM/yyyy";
  }

  getPublishedAtIso(): string {
    return this.metaSrv.resolvePublishIso(this.news);
  }
}
